<template>
  <component
    :is="tag"
    ref="control"
    class="sp-button"
    :class="classModifiers"
    part="base"
    :type="buttonType"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :aria-pressed="ariaPressed"
    v-bind="forwardableAttrs(attrs)"
    :aria-disabled="ariaDisabled"
    @click="onClick"
  >
    <div ref="prependSlot" class="sp-button__prepend" :class="prependSlotClassModifiers">
      <slot name="prepend">
        <sp-icon v-if="prependIcon" :name="prependIcon" size="small"></sp-icon>
      </slot>
    </div>
    <div class="sp-button__content" part="content">
      <div v-if="loading" class="sp-button__loading">
        <sp-progress type="ellipsis" size="xx-small" color="currentColor"></sp-progress>
      </div>
      <div v-if="hasIcon" class="sp-button__icon" part="icon">
        <sp-icon part="icon" :name="icon" :size="iconSize"></sp-icon>
      </div>
      <div v-else class="sp-button__label" part="label">
        <slot>
          {{ label }}
        </slot>
      </div>
    </div>
    <div ref="appendSlot" class="sp-button__append" :class="appendSlotClassModifiers">
      <slot name="append">
        <sp-icon v-if="appendIcon" :name="appendIcon" size="small"></sp-icon>
      </slot>
    </div>
  </component>
</template>

<script setup>
import { watchImmediate } from "@vueuse/core";
import { computed, ref, toRef, useAttrs, useHost, useTemplateRef, watch } from "vue";

import { useExpose } from "#ce/composables/useExpose";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useBooleanModifiers } from "#ce/composables/useModifiers";
import { useColor } from "#ce/composables/useColor";
import { sizePropValues, useSize } from "#ce/composables/useSize";
import { useVariant } from "#ce/composables/useVariant";
import { toBoolean } from "#ce/utils/props";
import { hasSlotContent } from "#ce/utils/slots";
import { formAssociatedHost } from "#ce/framework/utils";
import { forwardableAttrs } from "#ce/utils/forwarded-attrs";
import { useInlineLinkHost } from "#ce/components/SpButton/composables/useInlineLinkHost";
import { props as componentProps } from "./props";

// sp-button is form-associated so `type="submit"` / `type="reset"` can reach the owning form: the
// inner button's implicit activation stops at the shadow boundary.
// The host's attributes are forwarded by hand so their names survive: Vue's own fallthrough
// camelises them, which lowercases any aria-* or data-* name the DOM has no property for.
defineOptions({ ...formAssociatedHost(), inheritAttrs: false });

const props = defineProps(componentProps);
const { colorModifiers } = useColor(props);
const { variant, variantModifiers } = useVariant(props);
const { sizeModifiers } = useSize(props);

// SpButton's `size` prop accepts values like "default" / "large" that are not
// in SpIcon's allowed sizes. Forwarding them as-is fails SpIcon's validator
// and prints a Vue warning. The icon takes its real size from CSS via
// `--icon-size: var(--size)` regardless, so only forward `size` when it
// happens to be a valid SpIcon size — otherwise let CSS drive it.
const iconSize = computed(() => (sizePropValues.includes(props.size) ? props.size : undefined));
const { booleanModifiers } = useBooleanModifiers(
  props,
  "block",
  "stacked",
  "loading",
  "noWrap",
  "densitysmcompact",
  "disabled",
);

// `icon` is a value prop (an icon name), so its modifier keys on a non-blank
// value rather than attribute presence — `icon=""` must not produce an icon
// button.
const hasIcon = computed(() => typeof props.icon === "string" && props.icon.trim().length > 0);

const { internals } = useForm();

// The inner button's implicit submission stops at the shadow boundary, so the element drives its
// form itself. `requestSubmit` runs the form's validation and fires `submit`, unlike `form.submit()`.
// An anchor carries no type, so the attribute is only bound for the button tag.
const buttonType = computed(() => (props.tag === "button" ? props.type : undefined));

function onClick() {
  if (disabled.value || props.tag !== "button") {
    return;
  }

  if (props.type === "submit") {
    internals?.form?.requestSubmit();
  } else if (props.type === "reset") {
    internals?.form?.reset();
  }
}

const host = useHost();
// Disabled is governed by the form-control composable: presence-based own attribute plus ancestor
// <fieldset disabled>. The element is form-associated, so the host matches :disabled from the
// cascade too, and a click must not reach the form then.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const loading = ref(toBoolean(props.loading));
watch(
  () => props.loading,
  (value) => {
    loading.value = toBoolean(value);
  },
);

const control = useTemplateRef("control");
const attrs = useAttrs();

const { exposeMethods, exposeProperties } = useExpose();

function focus(options) {
  control.value?.focus(options);
}
exposeMethods({ focus });

// `controlElement` is the focusable node the host stands for, and the node a caller writes ARIA on.
// The host forwards its own attributes inward, so an attribute set on the host also lands here.
exposeProperties({
  controlElement: {
    configurable: true,
    get: () => control.value,
  },
});

const ariaLabel = computed(() => {
  const name = props.accessibleName?.trim();

  if (name) {
    return name;
  }

  // An icon-only button renders no text, so its label becomes the name instead.
  if (hasIcon.value && props.label) {
    return props.label;
  }

  return undefined;
});

const ariaPressed = computed(() => {
  if (props.pressed === undefined) {
    return undefined;
  }
  return toBoolean(props.pressed) ? "true" : "false";
});

// The host's `aria-disabled` reaches the inner button as "true" or "false", a bare attribute
// counting as "true". It is bound after the forwarded attributes so this value wins.
const ariaDisabled = computed(() => {
  const value = attrs.ariaDisabled;

  if (value === undefined) {
    return undefined;
  }
  return value === "" || toBoolean(value) ? "true" : "false";
});

const { isSmScreen } = useBreakpoints();
const densitysmcompact = computed(() => toBoolean(props.densitysmcompact) && isSmScreen.value);
const density = computed(() => (densitysmcompact.value ? "compact" : props.density));
const isBlockSmActive = computed(() => toBoolean(props.blockSm) && isSmScreen.value);

function adjustBlockSmActiveCustomProp() {
  const value = isBlockSmActive.value ? 1 : 0;
  host?.style.setProperty("--block-sm-active", value.toString());
}

watchImmediate(isBlockSmActive, adjustBlockSmActiveCustomProp);
useInlineLinkHost(host, props, variant, isBlockSmActive);

const classModifiers = computed(() => [
  ...colorModifiers.value,
  ...variantModifiers.value,
  ...sizeModifiers.value,
  ...booleanModifiers.value,
  { "--icon": hasIcon.value },
  { [`--density-${density.value}`]: density.value !== "default" },
  { "--block-sm": isBlockSmActive.value },
]);

const prependSlot = useTemplateRef("prependSlot");
const hasPrependSlot = computed(() => hasSlotContent(prependSlot.value));
const prependSlotClassModifiers = computed(() => createContentClassModifier(hasPrependSlot.value));

const appendSlot = useTemplateRef("appendSlot");
const hasAppendSlot = computed(() => hasSlotContent(appendSlot.value));
const appendSlotClassModifiers = computed(() => createContentClassModifier(hasAppendSlot.value));

function createContentClassModifier(slot) {
  return { "--has-content": slot };
}
</script>

<style>
:host {
  --bg-color: var(--sp-comp-button-bg-color, #fefefe);
  --text-color: var(--sp-comp-button-text-color, #000);
  --border-color: var(--sp-comp-button-border-color, transparent);
  --hover-text-color: var(--sp-comp-button-default-hover-text-color, #333);
  --active-text-color: var(--sp-comp-button-default-active-text-color, var(--text-color));
  --active-bg-color: var(--sp-comp-button-default-active-bg-color, var(--bg-color));
  --active-border-color: var(--sp-comp-button-default-active-border-color, var(--border-color));
  --default-hover-bg-color: var(--sp-comp-button-default-hover-bg-color, var(--bg-color));
  --hover-bg-color: var(--sp-comp-button-hover-bg-color, var(--default-hover-bg-color));
  --hover-bg-opacity: var(--sp-comp-button-hover-bg-opacity, 0);
  --hover-border-color: var(--sp-comp-button-hover-border-color, var(--border-color, transparent));
  --border-width: var(--sp-comp-button-border-width, 0);
  --border-style: var(--sp-comp-button-border-style, solid);
  --button-border-radius: var(--sp-comp-button-border-radius, var(--sp-sys-shape-control));
  --icon-fill-color: var(--sp-comp-button-icon-fill-color, var(--text-color));
  --transition-time: 0.3s;
  --font-family: var(--sp-comp-button-font-family, var(--sp-sys-typescale-body-font-family));
  --font-weight: var(--sp-comp-button-font-weight, var(--sp-sys-typescale-body-font-weight));
  --font-size: var(--sp-comp-button-font-size, var(--sp-sys-typescale-body-font-size));
  --line-height: var(--sp-comp-button-line-height, var(--sp-sys-typescale-body-line-height));
  --font-short-default: var(--font-weight) var(--font-size) / var(--line-height) var(--font-family);
  --font-short: var(--sp-comp-button-font-short, var(--font-short-default));
  --letter-spacing: var(--sp-comp-button-letter-spacing, normal);
  --button-display: var(--sp-comp-button-display, inline-grid);
  --button-min-width: var(--sp-comp-button-min-width, minmax(fit-content, 9rem));
  --button-width: var(--sp-comp-button-width, auto);
  --button-default-height: var(--sp-comp-button-size, var(--sp-sys-form-field-min-height, 3rem));
  --size: var(--button-default-height);
  --button-min-height: var(--sp-comp-button-min-height, auto);
  --button-justify-content: var(--sp-comp-button-justify-content, center);
  --button-content-text-align: var(--sp-comp-button-content-text-align, center);
  --button-content-overflow: var(--sp-comp-button-content-overflow, visible);
  --button-gap: var(--sp-comp-button-gap, 0.5ch);
  --button-padding-inline: var(--sp-comp-button-padding-inline, 0);
  --button-padding-block: var(--sp-comp-button-padding-block, 1rem);
  --button-padding: var(--sp-comp-button-padding, var(--button-padding-inline) var(--button-padding-block));
  --button-focus-outline-offset: var(--sp-comp-button-focus-outline-offset, 0);
  --button-focus-text-decoration: var(--sp-comp-button-focus-text-decoration, none);
  --button-box-shadow: var(--sp-comp-button-box-shadow, none);
  display: var(--inline-link-display, inline-grid);
  width: var(--button-width);
  box-sizing: border-box;
  border-radius: var(--button-border-radius);
  border-width: 0;
}

:host([variant="text-inline"]),
:host([variant="appearance-link"]) {
  display: var(--inline-link-display, inline);
  --button-display: inline-flex;
}

:host([variant="appearance-link-inline"]) {
  display: var(--inline-link-display, inline);
}

:host([block]) {
  display: grid;
  min-width: 100%;
}

:host([block-sm]) {
  min-width: calc(var(--block-sm-active, 0) * 100%);
}
</style>

<style lang="scss" scoped>
@import "@ce/shared/colors";

.sp-button {
  align-items: center;
  background: var(--bg-color);
  border-color: var(--border-color);
  border-radius: var(--button-border-radius);
  border-style: var(--border-style);
  border-width: var(--border-width);
  box-shadow: var(--button-box-shadow);
  box-sizing: border-box;
  color: var(--text-color);
  cursor: pointer;
  display: var(--button-display);
  font: var(--font-short);
  grid-template-areas: "prepend content append";
  grid-template-columns: max-content auto max-content;
  height: var(--size);
  min-height: var(--button-min-height);
  justify-content: var(--button-justify-content);
  min-width: var(--button-min-width);
  width: var(--button-width);
  padding: var(--button-padding);
  position: relative;
  text-decoration: var(--sp-comp-button-text-decoration, none);
  text-transform: var(--sp-comp-button-text-transform, none);
  letter-spacing: var(--letter-spacing);

  &__label {
    display: flex;
    align-items: center;
  }

  // ::before controls the background hover
  // ::after controls the focus
  &::before,
  &::after {
    position: absolute;
    content: "";
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    opacity: 0;
    border-radius: inherit;
    box-sizing: border-box;
    transition: opacity var(--transition-time) linear;
  }

  // Element for Background hover
  &::before {
    background: var(--hover-bg-color);
  }

  &:active::before {
    background: var(--active-bg-color);
    border-color: var(--active-border-color);
    opacity: var(--hover-bg-opacity);
  }

  &:hover:not(.--disabled) {
    color: var(--hover-text-color);
    border-color: var(--hover-border-color);

    &::before {
      opacity: var(--hover-bg-opacity);
    }
  }

  &.--variant-outlined {
    --border-width: var(--sp-comp-button-outlined-border-width, 1px);
    --border-style: var(--sp-comp-button-outlined-border-style, solid);
    --inner-border-radius: calc(var(--button-border-radius) - var(--border-width));
    background: transparent;

    &::before {
      border-radius: var(--inner-border-radius);
    }

    &:focus,
    &:focus-visible,
    &:focus-within {
      &::after {
        outline-offset: calc(var(--button-focus-outline-offset) + var(--border-width));
      }
    }
  }

  &.--variant-text {
    --border-width: 0;
  }

  &.--variant-text-inline {
    &::after,
    &::before {
      top: -0.75ex;
      right: -0.5ch;
      bottom: -0.75ex;
      left: -0.5ch;
    }
  }

  &.--variant-text-inline,
  &.--variant-appearance-link,
  &.--variant-appearance-link-inline {
    --button-min-height: auto;
    padding: 0 !important;
    height: auto !important;
    min-width: 0 !important;
    gap: 0 !important;
  }

  &.--variant-appearance-link,
  &.--variant-appearance-link-inline {
    --bg-color: transparent !important;
    --hover-bg-opacity: var(--sp-comp-button-appearance-link-hover-bg-opacity, 0);
    --text-color: var(--sp-comp-button-appearance-link-text-color, var(--sp-sys-color-link, #007078)) !important;
    --hover-text-color: var(--sp-comp-button-appearance-link-hover-text-color, var(--text-color)) !important;
    --border-color: transparent;
    --border-width: 0;
    --border-style: none;
    --button-display: inline-flex;
    text-decoration: var(--sp-comp-button-appearance-link-text-decoration, underline);

    &::before,
    &::after {
      display: none;
    }

    &:focus-visible {
      outline: var(
        --sp-comp-button-focus-visible-outline,
        var(
          --sp-comp-button-focus-outline,
          var(--sp-sys-state-focus-indicator-thickness) solid var(--sp-sys-color-primary)
        )
      );
      outline-offset: var(
        --sp-comp-button-focus-visible-outline-offset,
        var(--sp-comp-button-focus-outline-offset, var(--sp-sys-state-focus-indicator-outer-offset))
      );
    }
  }

  &.--block-sm,
  &.--block {
    width: 100%;
  }

  &.--stacked {
    --button-display: inline-grid;
    grid-template-areas: "prepend" "content" "append";
    justify-items: center;
    min-height: fit-content;
    height: auto;

    .sp-button__content,
    .sp-button__label,
    .sp-button__icon {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  &[aria-disabled="true"] {
    cursor: var(--sp-comp-button-disabled-cursor, default);
  }

  &.--loading {
    cursor: wait;
    .sp-button__label,
    .sp-button__icon {
      opacity: 0;
    }
  }

  &.--no-wrap {
    white-space: nowrap;
  }

  // Sizes
  &.--size-xx-small {
    --size: var(--sp-comp-button-xx-small-height, 1.25rem);
    font-size: var(--sp-comp-button-xx-small-font-size, 0.5rem);
    padding: var(--sp-comp-button-xx-small-padding, 0 0.25rem);
  }

  &.--size-x-small {
    --size: var(--sp-comp-button-x-small-height, 1.5rem);
    font-size: var(--sp-comp-button-x-small-font-size, 0.75rem);
    padding: var(--sp-comp-button-x-small-padding, 0 0.5rem);
  }

  &.--size-small {
    --size: var(--sp-comp-button-small-height, 2rem);
    font: var(--sp-comp-button-small-font-short, var(--sp-sys-typescale-small-bold-short));
    padding: var(--sp-comp-button-small-padding, 0 0.75rem);
  }

  &.--size-large {
    --size: var(--sp-comp-button-large-height, 3.75rem);
    font-size: var(--sp-comp-button-large-font-size, 1.375rem);
    padding: var(--sp-comp-button-large-padding, 0 1.875rem);
  }

  &.--icon {
    --button-content-overflow: hidden;
    --button-border-radius: var(--sp-comp-button-icon-border-radius, 50%);
    --icon-size: var(--sp-comp-button-icon-size, var(--size));
    display: grid;
    padding: 0;
    min-width: var(--size);
    width: var(--size);
    min-height: var(--size);
    max-height: var(--size);
    justify-content: center;
    align-items: center;
    grid-template-areas: "content";
    grid-template-columns: auto;
    grid-template-rows: auto;

    sp-icon {
      --size: var(--icon-size);
      --fill: var(--sp-comp-button-icon-fill-color, var(--text-color));
    }

    .sp-button__content,
    .sp-button__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .sp-button__prepend,
    .sp-button__append {
      display: none;
    }
  }

  &.--density-compact {
    --size: var(
      --sp-comp-button-compact-height,
      var(--sp-sys-form-field-compact-min-height, var(--button-default-height))
    );
  }

  // Follows the size and no-wrap rules so the sentence's font and wrapping win over them.
  &.--variant-appearance-link-inline {
    --button-display: inline;
    font: inherit;
    letter-spacing: inherit;
    text-transform: inherit;
    white-space: inherit;

    .sp-button__prepend,
    .sp-button__content,
    .sp-button__label,
    .sp-button__append {
      display: inline;
    }
  }

  // Colors
  @each $color in primary, secondary, danger, text, error, warning, success, info, "white", custom, "transparent",
    surface
  {
    &.--color-#{$color} {
      --text-color: var(--sp-comp-button-#{$color}-text-color, var(--sp-sys-color-on-#{$color}, #fff));
      --bg-color: var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #{get-color($color)}));
      --active-bg-color: var(--sp-comp-button-#{$color}-active-bg-color, var(--bg-color));
      --active-text-color: var(--sp-comp-button-#{$color}-active-text-color, var(--text-color));
      --active-border-color: var(--sp-comp-button-#{$color}-active-border-color, var(--border-color));
      --default-hover-text-color: var(--sp-sys-color-on-#{$color}, #fff);
      --hover-text-color: var(--sp-comp-button-#{$color}-hover-text-color, var(--default-hover-text-color));
      --hover-bg-color: var(--sp-comp-button-#{$color}-hover-bg-color, var(--default-hover-bg-color));
      --icon-fill-color: var(--text-color);

      &.--variant-text-inline,
      &.--variant-text {
        --bg-color: transparent;
        --text-color: var(
          --sp-comp-button-#{$color}-variant-text-text-color,
          var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #666))
        );
        --hover-text-color: var(--sp-comp-button-#{$color}-hover-variant-text-text-color, var(--text-color));
        --hover-bg-color: var(
          --sp-comp-button-#{$color}-hover-outlined-bg-color,
          var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #666))
        );
        --hover-bg-opacity: var(--sp-comp-button-#{$color}-hover-variant-text-bg-opacity, 0.1);
      }

      &.--variant-outlined {
        --text-color: var(
          --sp-comp-button-#{$color}-outlined-text-color,
          var(--sp-comp-button-#{$color}-bg-color, var(--sp-sys-color-#{$color}, #666))
        );
        --icon-fill-color: var(--text-color);
        --border-color: var(--sp-comp-button-outlined-border-color, var(--text-color));
        --hover-text-color: var(--sp-comp-button-#{$color}-hover-outlined-text-color, var(--default-hover-text-color));
        --hover-bg-color: var(--sp-comp-button-#{$color}-hover-outlined-bg-color, var(--bg-color));
        --hover-bg-opacity: var(--sp-comp-button-#{$color}-hover-outlined-bg-opacity, 1);
        --hover-border-color: var(
          --sp-comp-button-hover-outlined-border-color,
          var(--sp-comp-button-#{$color}-outlined-hover-border-color, var(--border-color))
        );

        --active-bg-color: var(--sp-comp-button-#{$color}-active-outlined-bg-color, transparent);
        --active-text-color: var(--sp-comp-button-#{$color}-active-outlined-text-color, var(--text-color));
        --active-border-color: var(--sp-comp-button-#{$color}-active-outlined-border-color, var(--border-color));

        &:focus:not(.--disabled),
        &:focus-visible:not(.--disabled),
        &:focus-within:not(.--disabled) {
          --border-color: var(--sp-comp-button-focus-outlined-border-color, var(--text-color));
        }

        &:hover:not(.--disabled) {
          --icon-fill-color: var(--hover-text-color);
          --active-text-color: var(--hover-text-color);
          --active-bg-color: var(--hover-bg-color);
          --active-border-color: var(--hover-border-color);

          sp-icon {
            --fill: var(--hover-text-color);
          }

          ::slotted(sp-icon) {
            --fill: var(--hover-text-color);
          }
        }

        &:active {
          --text-color: var(--active-text-color);
        }
      }
    }
  }

  // `surface` inverts the text/outlined treatment: the loop derives the text,
  // border and hover-tint "ink" from --sp-sys-color-{color}, which for surface
  // is the background colour. Point those at the on-surface foreground instead
  // so the ink stays legible against a surface-coloured backdrop.
  //
  // The loop's shared `#fff` fallback is also wrong for surface (white text on a
  // white surface if --sp-sys-color-on-surface is ever undefined), so override
  // the flat text/hover fallbacks to a dark on-surface value too.
  &.--color-surface {
    --text-color: var(--sp-comp-button-surface-text-color, var(--sp-sys-color-on-surface, #1e293b));
    --default-hover-text-color: var(--sp-sys-color-on-surface, #1e293b);

    &.--variant-text-inline,
    &.--variant-text {
      --text-color: var(--sp-comp-button-surface-variant-text-text-color, var(--sp-sys-color-on-surface, #1e293b));
      --hover-bg-color: var(--sp-comp-button-surface-hover-outlined-bg-color, var(--sp-sys-color-on-surface, #1e293b));
    }

    &.--variant-outlined {
      --text-color: var(--sp-comp-button-surface-outlined-text-color, var(--sp-sys-color-on-surface, #1e293b));
    }
  }

  &:focus,
  &:focus-visible,
  &:focus-within {
    outline: none;
    box-shadow: var(--button-box-shadow);
    text-decoration: var(--button-focus-text-decoration);
  }

  &:focus,
  &:focus-within {
    &::after {
      opacity: var(--sp-comp-button-focus-opacity, 1);
      outline: var(--sp-comp-button-focus-outline, 1px solid #ccc);
      outline-offset: var(--sp-comp-button-focus-outline-offset, 0);
      box-shadow: var(--sp-comp-button-focus-box-shadow, none);
      border-radius: var(--sp-comp-button-focus-border-radius, none);
    }
  }

  // enable special syling for key-focus if needed
  &:focus-visible::after {
    box-shadow: var(--sp-comp-button-focus-visible-box-shadow, var(--sp-comp-button-focus-box-shadow, none));
    outline: var(--sp-comp-button-focus-visible-outline, var(--sp-comp-button-focus-outline, 1px solid #ccc));
    outline-offset: var(--sp-comp-button-focus-visible-outline-offset, var(--sp-comp-button-focus-outline-offset, 0));
    opacity: var(--sp-comp-button-focus-visible-opacity, var(--sp-comp-button-focus-opacity, 1));
  }

  &.--disabled {
    --disabled-text-color: var(--sp-comp-button-disabled-text-color, var(--text-color));
    --disabled-background-color: var(--sp-comp-button-disabled-background-color, var(--sp-sys-color-disabled));
    --disabled-icon-fill: var(
      --sp-comp-button-disabled-icon-fill-color,
      var(--sp-comp-button-icon-fill-color, var(--icon-fill-color))
    );
    --sp-comp-icon-fill: var(--disabled-icon-fill);

    background: var(--disabled-background-color);

    &::before,
    &::after {
      opacity: 0 !important;
    }

    color: var(--disabled-text-color);
    --icon-fill-color: var(--disabled-text-color);
    cursor: var(--sp-comp-button-disabled-cursor, default);
    opacity: var(--sp-comp-button-disabled-opacity, 1);

    sp-icon {
      --fill: var(--disabled-icon-fill);
    }

    &.--variant-outlined,
    &.--variant-text-inline,
    &.--variant-text {
      background: transparent;
      color: var(--disabled-background-color);
      --icon-fill-color: var(--disabled-background-color);
    }

    &.--variant-outlined {
      --border-color: var(--disabled-background-color);
    }
  }
}

.sp-button__content {
  grid-area: content;
  z-index: 1;
  text-align: var(--button-content-text-align);
  overflow: var(--button-content-overflow);
}

.sp-button__prepend,
.sp-button__append {
  display: flex;
  position: relative;
  z-index: 1;
}

.sp-button__prepend {
  --sp-comp-icon-fill: var(--sp-comp-button-prepend-icon-fill, var(--icon-fill-color));
  --sp-comp-icon-size: var(--sp-comp-button-prepend-icon-size, 2rem);

  grid-area: prepend;
  left: calc(var(--button-gap) * -1);
}

.sp-button__append {
  --sp-comp-icon-fill: var(--sp-comp-button-append-icon-fill, var(--icon-fill-color));
  --sp-comp-icon-size: var(--sp-comp-button-append-icon-size, 2rem);

  grid-area: append;
  right: calc(var(--button-gap) * -1);
}

.sp-button__loading {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
}
</style>
